'use client';

import { useEffect } from 'react';

import { APP_VERSION_LABEL } from '@/shared/constants/appVersion';
import { claimRecovery, DEAD_TREE_KEY, isAppAsset } from '@/shared/libs/assetRecovery';
import { isDeadTreeError, reviveOnDeadTree } from '@/shared/libs/deadTree';
import logger from '@/shared/libs/logger.client';

/**
 * Journalise les erreurs que **personne n'attrape** (#1734).
 *
 * Le figeage de navigation de #1732 levait `Cannot read properties of null (reading 'removeChild')`
 * — `Uncaught`. Le filet `AppError` ne voit que les erreurs de **rendu** : celle-ci survient en
 * phase de commit, hors de sa portée. L'écran se figeait donc **sans message et sans trace**, et le
 * seul témoignage était la console de celui qui l'avait subi.
 *
 * Trois causes différentes ont produit ce même écran figé le 24/08 — une zone morte temporelle
 * (#1726), un écart de version après mise en service (#1722), une traduction automatique (#1732).
 * Elles ne se distinguaient que par leur message, et ce message n'était nulle part.
 *
 * **On observe, on ne rattrape pas — sauf une.** Tenter de « réparer » une erreur inconnue la
 * ferait se reproduire en boucle sans qu'on sache pourquoi. `AssetRecovery` recharge, parce qu'il
 * sait exactement ce qu'il traite.
 *
 * Depuis #1745, une signature est connue à ce point : l'échec d'une opération de commit React sur
 * un nœud disparu, qui **tue l'arbre**. Plus aucun rendu ne repartira, la frontière `AppError` ne
 * la voit pas, et l'écran garde ce qui était peint — en production, un chargeur qui tourne après
 * la connexion. On recharge alors une fois, ce que la personne finit par faire elle-même. Les
 * autres erreurs continuent d'être écrites, et rien de plus.
 */

/**
 * Nombre de traces par session.
 *
 * Une erreur de commit React se répète à **chaque** tentative de rendu : sans plafond, une seule
 * anomalie produirait des centaines de lignes et noierait ce qu'elle explique — le défaut même
 * qu'on vient de corriger sur les traductions (#1730).
 */
const MAX_PER_SESSION = 5;

export function UncaughtErrorLog() {
  useEffect(() => {
    let written = 0;

    /*
      **Recharger, une fois.** Le refus est le cas important : il dit que le rechargement n'a pas
      suffi, donc que la cause survit à la page — c'est cette trace-là qui mènera à elle.

      Une saisie en cours est perdue ; elle l'était déjà. Un arbre mort ne reçoit plus aucun
      événement : ce qui est à l'écran n'est plus qu'une image.
    */
    const revive = (message: string, stack?: string) => {
      const outcome = reviveOnDeadTree(
        {
          message,
          stack,
          now: Date.now(),
          session: window.sessionStorage,
          path: window.location.pathname,
          version: APP_VERSION_LABEL,
          sinceLoadMs: Math.round(performance.now()),
        },
        claimRecovery,
        DEAD_TREE_KEY,
      );
      logger[outcome.level](outcome.label, outcome.meta);
      // L'adresse n'a pas bougé : on rouvre l'écran demandé, pas le précédent.
      if (outcome.action === 'reload') window.location.reload();
    };

    const write = (source: 'error' | 'rejection', message: string, stack?: string) => {
      if (isDeadTreeError(message)) {
        revive(message, stack);
        return;
      }
      if (written >= MAX_PER_SESSION) return;
      written += 1;
      logger.error('[uncaught] erreur non rattrapée', {
        source,
        message,
        stack,
        /* La pile dit où le code a levé ; elle ne dit pas ce que la personne regardait. */
        path: window.location.pathname,
        version: APP_VERSION_LABEL,
        ...(written === MAX_PER_SESSION
          ? { note: `Plafond atteint : les suivantes de cette session ne seront pas journalisées.` }
          : {}),
      });
    };

    const onError = (event: ErrorEvent) => {
      /*
        Un `error` porté par un élément est un **échec de chargement de ressource**, pas une
        exception : `AssetRecovery` s'en occupe et le journalise déjà. Le doubler ferait deux lignes
        pour un même incident, et l'une des deux dirait quelque chose de faux.
      */
      if (event.target && event.target !== window) return;
      const err = event.error as Error | undefined;
      write('error', err?.message ?? event.message ?? 'Erreur sans message', err?.stack);
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason as
        | { name?: string; message?: string; stack?: string }
        | undefined;
      /* `ChunkLoadError` appartient à `AssetRecovery`, qui recharge la page en le voyant. */
      if (reason?.name === 'ChunkLoadError') return;
      if (typeof reason?.message === 'string' && isAppAsset(reason.message)) return;
      write('rejection', reason?.message ?? String(event.reason), reason?.stack);
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
}

export default UncaughtErrorLog;
